type ChatwootSettings = {
  locale?: string;
};

declare global {
  interface Window {
    chatwootSettings?: ChatwootSettings;
    chatwootSDK?: { run: (opts: { websiteToken: string; baseUrl: string }) => void };
    $chatwoot?: {
      toggle: (state?: "open" | "close") => void;
      setLocale: (locale: string) => void;
      setUser: (identifier: string, data: Record<string, unknown>) => void;
      setCustomAttributes: (data: Record<string, unknown>) => void;
      reset: () => void;
    };
  }
}

let loadPromise: Promise<void> | null = null;

const safeId = (value: string): string => value.trim().toLowerCase();


const getConfig = (): { baseUrl: string; token: string } | null => {
  const baseUrl = process.env.VUE_APP_CHATWOOT_BASE_URL as string | undefined;
  const token = process.env.VUE_APP_CHATWOOT_WEBSITE_TOKEN as string | undefined;

  return baseUrl && token ? { baseUrl, token } : null;
};

const waitForChatwoot = (timeoutMs: number): Promise<void> => new Promise((resolve, reject) => {
  const startedAt = Date.now();

  const tick = (): void => {
    if (window.$chatwoot) {
      resolve();
      return;
    }

    (Date.now() - startedAt) >= timeoutMs
      ? reject(new Error("Chatwoot did not initialize (window.$chatwoot missing)"))
      : window.setTimeout(tick, 50);
  };

  tick();
});


export const ChatwootService = {
  async ensureLoaded(locale: string): Promise<void> {
    const cfg = getConfig();
    if (!cfg) return;

    if (window.$chatwoot) {
      window.$chatwoot.setLocale(locale);
      return;
    }

    loadPromise = loadPromise ?? new Promise<void>((resolve, reject) => {
      window.chatwootSettings = { locale };

      const script = document.createElement("script");
      script.src = `${cfg.baseUrl}/packs/js/sdk.js`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        window.chatwootSDK?.run({ websiteToken: cfg.token, baseUrl: cfg.baseUrl });
        resolve();
      };

      script.onerror = () => reject(new Error("Failed to load Chatwoot SDK"));

      document.head.appendChild(script);
    });

    await loadPromise;
    await waitForChatwoot(5000);
  },

  setContact(email: string, contractId: string): void {
    if (!window.$chatwoot) return;

    const id = safeId(email || contractId || "guest");

    window.$chatwoot.setUser(id, {
      email
    });

    window.$chatwoot.setCustomAttributes({
      reservation_id: contractId,
      reservation_reference: contractId
    });
  },

  open(): void {
    window.$chatwoot?.toggle("open");
  },

  close(): void {
    window.$chatwoot?.toggle("close");
  }
};
